import type RootStore from "stores/RootStore";
import { RouteType } from "types/RouteType";
import { throwIfFailed } from "shared/dialogSubmit";
import TicketDeskStore from "./TicketDeskStore";

/**
 * Tickets — the desk where a developer or salesperson asks a manager (or a
 * team) for a decision, an approval, an asset, access or information, and
 * where the person asked works through what is waiting on them.
 *
 * `?open=<id>` opens one ticket in the drawer: the bell, a phone push and
 * Gantry's feed all deep-link here. `?scope=mine|asked|team|all` opens a tab
 * (the default is "Waiting on me"): the dashboards' "Tickets past their clock"
 * counts every ticket and links to `?scope=all`. `?pickup=waiting` turns on
 * "Not picked up", which the dashboards' "Tickets nobody picked up" links to.
 */
const ticketsRoute: RouteType[] = [
  {
    name: "Tickets",
    path: "/tickets",
    layout: "/app",
    viewportCard: {
      header: {
        enableBackButton: true,
        title: "Tickets",
        enableRefreshButton: true,
        component: () => import("./Tickets.Header"),
      },
      body: {
        component: () => import("./Tickets.Body"),
        grayBackground: true,
      },
    },
    group: "Clients",
    searchAliases: ["requests", "asks", "waiting on me", "approvals", "tkt"],
    requiresAuth: true,
    requiresPermission: false,
    onEnter: ({ rootStore, manualTrigger }) => {
      const store = rootStore.ticketDeskStore as TicketDeskStore;
      if (!store) return;
      // Other people's changes re-read the desk while it is open (#3820).
      store.watchLive();
      void store.loadUsers();
      // The header's refresh re-runs this hook with `manualTrigger`: the URL still says `scope=all`
      // while the person may have moved to another tab since, and a refresh must not put them back.
      if (!manualTrigger) {
        store.showScopeFromLink(rootStore.routerStore?.queryParams?.scope);
        store.showPickupFromLink(rootStore.routerStore?.queryParams?.pickup);
      }
      const raw = rootStore.routerStore?.queryParams?.open;
      const open = Number(Array.isArray(raw) ? raw[0] : raw);
      void store.load().then(() => {
        if (Number.isFinite(open) && open > 0) void store.open(open);
      });
    },
    onExit: ({ rootStore }) => {
      (rootStore.ticketDeskStore as TicketDeskStore | undefined)?.unwatchLive();
    },
    dialogs: [
      {
        name: "NewTicketDialog",
        title: "New ticket",
        submitButtonText: "Create ticket",
        component: () => import("./dialogs/NewTicketDialog.Body"),
        cancelButton: true,
        height: 640,
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          const store = rootStore.ticketDeskStore as TicketDeskStore;
          // A refused filing REJECTS (submitNewTicket throws the server's
          // reason), so the dialog stays open with it in its footer. A null
          // with no throw means nothing was sent — a desk with no signed-in
          // account behind it — and must not close as if it had been filed.
          // (A re-entered press while one is in flight also returns null; the
          // gate below disables the button then, so it is not a refusal.)
          const created = await store.submitNewTicket();
          if (!created && !store.submitting) {
            throwIfFailed(false, store.wiringError, "The ticket could not be filed.");
          }
        },
        // The library's Dialog.Footer disables Submit from THIS and nothing
        // else — its onClick awaits onSubmit with no pending state of its own,
        // so without `submitting` here a second click during the await reaches
        // the server again. The store refuses the re-entry regardless; this is
        // what makes the button say so.
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
          const store = rootStore.ticketDeskStore as TicketDeskStore;
          return store.draftValid && !store.submitting;
        },
      },
    ],
  },
];

export default ticketsRoute;
